import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, IconNames } from '@triumph/design-system';
import { useDebtorAccount } from '@/app/shared/utils/hooks/useDebtorAccount';
import { PageRoutes } from '@/app/components/router/routes';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import LoadingPage from '@/app/shared/utils/LoadingPage';
import {
  useCarrierNoaDetail,
  useSearchCarriers,
} from '@/app/shared/api/endpoints/noaCenter';
import { NOACenterHeader } from './NOACenterHeader';
import { CarrierSearch } from './components/CarrierSearch';
import { CarrierList } from './components/CarrierList';
import { CarrierDetailPanel } from './components/CarrierDetailPanel';
import {
  BackLink,
  ContentContainer,
  PageContainer,
  PageDescription,
} from './NOACenter.StyledComponents';

export const NOACenter: React.FC = () => {
  const { t } = useTranslation('noaCenter');
  const navigate = useNavigate();
  const { theme } = useAppContext();
  const {
    selectedDebtor,
    initialized: isDebtorInitialized,
    isDebtorSwitching,
  } = useDebtorAccount();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCarrierId, setSelectedCarrierId] = useState<string | null>(null);

  const isInitialized = isDebtorInitialized && !!selectedDebtor;

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 800);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const { data: carriers = [], isFetching: isSearching } =
    useSearchCarriers(debouncedSearchTerm);
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useCarrierNoaDetail(selectedCarrierId);

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setSelectedCarrierId(null);
  };

  if (isDebtorSwitching || !isInitialized) {
    return <LoadingPage />;
  }

  return (
    <PageContainer>
      <BackLink
        onClick={() => navigate(PageRoutes.DASHBOARD)}
        data-testid="noa-back-to-dashboard"
      >
        <Icon
          name={IconNames.Bold_ArrowLeft}
          id="noa-back-arrow"
          size="sm"
          color={theme.colors.greyscale.gray40}
        />
        {t('header.backToDashboard')}
      </BackLink>
      <NOACenterHeader />
      <PageDescription>{t('header.description')}</PageDescription>

      <CarrierSearch
        value={searchTerm}
        onChange={handleSearchChange}
        resultCount={carriers.length}
        showCount={!isSearching}
      />

      <ContentContainer>
        <CarrierList
          carriers={carriers}
          selectedCarrierId={selectedCarrierId}
          onSelect={setSelectedCarrierId}
          isLoading={isSearching}
          hasSearched
        />
        <CarrierDetailPanel
          detail={detail}
          isLoading={isDetailLoading}
          isError={isDetailError}
          hasSelection={!!selectedCarrierId}
        />
      </ContentContainer>
    </PageContainer>
  );
};
